import { invoiceUrls } from "../invoices/urls.js";

/**
 * 全角の空白。見出しの区切りに使う（企業名と契約 ID の間など）。
 * <b>ソースに直接書かない</b>——見えない文字は ESLint が断る（no-irregular-whitespace）。
 */
export const wideSpace = String.fromCodePoint(0x30_00);

/** 画面の URL に載せる条件（欄の名前 → 値）。 */
type Query = Readonly<Record<string, string | number | null>>;

/** クエリを付ける。<b>空の値は載せない</b>（今のアプリでも空は「条件なし」と同じ）。 */
const withQuery = (path: string, query: Readonly<Record<string, string | number | null | undefined>>): string => {
    const search = new URLSearchParams();
    for (const [name, value] of Object.entries(query)) {
        if (value !== null && value !== undefined && value !== "") {
            search.append(name, String(value));
        }
    }
    const text = search.toString();
    return text === "" ? path : `${path}?${text}`;
};

/**
 * 管理センターの画面の URL。<b>今のアプリと同じ形</b>（<c>/AdminCenter/Tenant/5</c> など）。
 *
 * <b>クリアは「条件の無い URL を開く」</b>——ここにある素の URL へのリンクにする
 * （<c>type="reset"</c> は描いたときの値に戻すだけ。CLAUDE.md「管理センターの検索」）。
 */
export const adminCenterUrls = {
    menu: "/AdminCenter",
    tenants: "/AdminCenter/Tenants",
    tenant: (id: number | string, query: Query = {}): string =>
        withQuery(`/AdminCenter/Tenant/${encodeURIComponent(String(id))}`, query),
    contractor: (id: number | string): string => `/AdminCenter/Tenant/${encodeURIComponent(String(id))}/Contractor`,
    mails: "/AdminCenter/Mails",
    mailsOfTenant: (tenantId: number): string => withQuery("/AdminCenter/Mails", { tenantId }),
    mail: (id: number): string => `/AdminCenter/Mail/${String(id)}`,
    permissions: "/AdminCenter/Permissions",
    lockout: "/AdminCenter/Lockout",
    /** 契約の復元（解約から 30 日以内の契約。Implem.Portal.Backend の docs/features/38）。 */
    restore: "/AdminCenter/Restore",
    activity: (query: Query = {}): string =>
        withQuery("/AdminCenter/Activity", query),
    activityOfTenant: (tenantId: number): string => withQuery("/AdminCenter/Activity", { tenantId }),
    payments: (query: Query = {}): string =>
        withQuery("/AdminCenter/Payments", query),
    /** PAY.JP の課金（毎月の請求。PAY.JP から取り込んだ記録）。 */
    charges: (query: Query = {}): string =>
        withQuery("/AdminCenter/Charges", query),
    chargesOfTenant: (tenantId: number): string => withQuery("/AdminCenter/Charges", { tenantId }),
    invoices: (tenantId: number): string => invoiceUrls.listFromCenter(tenantId),
    /** 見積の検索（料金プランのページから作られた見積書。社内メールのリンクは 1 通の画面へ来る）。 */
    estimates: (query: Query = {}): string =>
        withQuery("/AdminCenter/Estimates", query),
    estimate: (id: number | string): string => `/AdminCenter/Estimate/${encodeURIComponent(String(id))}`,
} as const;

/** CSV の口に載せる条件（今の結果の条件。空の値は載せない）。 */
type CsvQuery = Parameters<typeof withQuery>[1];

/**
 * 管理センターの API（AdminCenterApiController）。<b>画面のクエリはそのまま渡す</b>——条件を読むのはサーバ。
 *
 * <b>CSV（<c>activityCsv</c>・<c>mailsCsv</c>・<c>chargesCsv</c>・<c>estimatesCsv</c>・<c>invoiceCsv</c>）は JSON でなくファイル</b>で、受け取って保存させる（<c>api/download.ts</c>。<c>getJson</c> では読まない）。
 * 今の結果の条件（サーバが返した値）をそのままクエリに載せる。
 */
export const adminCenterApi = {
    menu: "/api/admin-center",
    tenants: (search: string): string => `/api/admin-center/tenants${search}`,
    tenant: (id: string, search: string): string =>
        `/api/admin-center/tenants/${encodeURIComponent(id)}${search}`,
    mails: (search: string): string => `/api/admin-center/mails${search}`,
    mail: (id: string): string => `/api/admin-center/mails/${encodeURIComponent(id)}`,
    permissions: "/api/admin-center/permissions",
    lockout: (search: string): string => `/api/admin-center/lockout${search}`,
    unlock: "/api/admin-center/unlock",
    resetTwoFactor: "/api/admin-center/two-factor/reset",
    activity: (search: string): string => `/api/admin-center/activity${search}`,
    payments: (search: string): string => `/api/admin-center/payments${search}`,
    charges: (search: string): string => `/api/admin-center/charges${search}`,
    /** PAY.JP からの取り込みを受け付けさせる（取り込みは常駐処理が後ろで動かす。PAY.JP には書かない）。 */
    chargesImport: "/api/admin-center/charges/import",
    chargesCsv: (query: CsvQuery): string => withQuery("/api/admin-center/charges/csv", query),
    activityCsv: (query: CsvQuery): string => withQuery("/api/admin-center/activity/csv", query),
    mailsCsv: (query: CsvQuery): string => withQuery("/api/admin-center/mails/csv", query),
    estimates: (search: string): string => `/api/admin-center/estimates${search}`,
    estimatesCsv: (query: CsvQuery): string => withQuery("/api/admin-center/estimates/csv", query),
    estimate: (id: string): string => `/api/admin-center/estimates/${encodeURIComponent(id)}`,
    /**
     * 請求の CSV（全契約の 1 か月分。<c>TenantInvoiceCsvController</c>）。<b>URL は今のアプリと同じ形</b>（運営の手順に載っている）。
     * 契約の番号は口が使わないので 0。
     */
    invoiceCsv: (month: string): string => withQuery("/api/TenantInvoice/0/Csv", { id: month }),
    invoices: (tenantId: number): string => `/api/admin-center/tenants/${String(tenantId)}/invoices`,
    regenerateInvoice: (tenantId: number, month: string): string =>
        `/api/admin-center/tenants/${String(tenantId)}/invoices/${encodeURIComponent(month)}/regenerate`,
    invoiceSettings: (tenantId: number): string => `/api/admin-center/tenants/${String(tenantId)}/invoice-settings`,
    invoiceExemption: (tenantId: number): string => `/api/admin-center/tenants/${String(tenantId)}/invoice-exemption`,
    cancel: (tenantId: number): string => `/api/admin-center/tenants/${String(tenantId)}/cancel`,
    /** 復元できる契約（解約から 30 日以内）と、1 件の復元。 */
    restorations: "/api/admin-center/restorations",
    restore: (tenantId: number): string => `/api/admin-center/tenants/${String(tenantId)}/restore`,
    /** 新しい契約者に選べる人（画面のクエリ <c>?keyword=</c> をそのまま渡す）。 */
    contractor: (id: string, search: string): string =>
        `/api/admin-center/tenants/${encodeURIComponent(id)}/contractor${search}`,
    changeContractor: (tenantId: number): string => `/api/admin-center/tenants/${String(tenantId)}/contractor`,
    /** 利用者を招待する（欄は会員の画面の招待と同じ <c>Email</c>・<c>Admin</c>）。 */
    inviteUser: (tenantId: number): string => `/api/admin-center/tenants/${String(tenantId)}/users/invite`,
} as const;
